import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MechanicalChecklistForm } from "@/components/MechanicalChecklist";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  ArrowLeft, Save, Loader2, Wrench, CheckCircle, Info
} from "lucide-react";

export default function MechanicFichePage() {
  const { scooterId } = useParams();
  const [searchParams] = useSearchParams();
  const programareId = searchParams.get("programare");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [scooter, setScooter] = useState(null);
  const [mechanicalCheck, setMechanicalCheck] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchScooter();
  }, [scooterId]);

  const fetchScooter = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/scooters/${scooterId}`);
      const data = resp.data;
      setScooter(data);
      setMechanicalCheck(data.mechanical_check || {});
    } catch (err) {
      toast.error("Eroare la incarcarea scuterului");
    } finally { setLoading(false); }
  };

  const handleCheckChange = (newValues) => {
    setMechanicalCheck(newValues);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/scooters/${scooterId}`, {
        mechanical_check: mechanicalCheck,
      });
      setHasChanges(false);
      toast.success("Fisa tehnica salvata cu succes!");

      // Log mechanic activity
      try {
        await api.post(`/mechanic-history/log?scooter_id=${scooterId}&action=mechanical_check_saved&details=${encodeURIComponent(programareId ? `Programare: ${programareId}` : 'Modificare directa')}`);
      } catch (err) {
        console.error("History log error:", err);
      }

      // Mark programare as verified if coming from a programare
      if (programareId) {
        try {
          await api.put(`/programari/${programareId}`, { status: "verificat", mechanic_notes: `Verificat de ${user?.username || 'mecanic'}` });
        } catch (err) {
          console.error("Programare update error:", err);
        }
      }
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Eroare la salvare");
    } finally { setSaving(false); }
  };

  const scooterInfo = scooter ? {
    scooter_id: scooter.scooter_id || scooterId,
    brand: scooter.brand,
    model: scooter.model,
    year: scooter.year,
    vin: scooter.vin,
    mileage: scooter.mileage,
  } : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!scooter) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-medium">Scuter negasit</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Inapoi
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="mechanic-fiche-page">
      {/* Sticky header */}
      <div className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate("/mechanic-dashboard")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-bold text-sm truncate">{scooter.brand} {scooter.model}</h1>
              <p className="text-xs text-muted-foreground font-mono">{scooter.scooter_id} &bull; {scooter.year} &bull; {scooter.mileage?.toLocaleString()} km</p>
            </div>
          </div>
          <Button onClick={handleSave} disabled={saving || !hasChanges} 
            className={hasChanges ? "bg-green-600 hover:bg-green-700" : ""} 
            data-testid="save-fiche-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? "Salvare..." : hasChanges ? "Salveaza" : "Salvat"}
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Scooter info card */}
        <Card className="border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-lg bg-secondary overflow-hidden flex-shrink-0">
                {scooter.photos && scooter.photos[0] ? (
                  <img src={scooter.photos[0]} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><Wrench className="w-6 h-6 text-muted-foreground" /></div>
                )}
              </div>
              <div>
                <h2 className="font-bold">{scooter.brand} {scooter.model}</h2>
                <p className="text-sm text-muted-foreground">
                  {scooter.year} &bull; {scooter.engine_cc}cc &bull; {scooter.color} &bull; {scooter.mileage?.toLocaleString()} km
                </p>
                <p className="text-xs text-muted-foreground font-mono mt-0.5">
                  VIN: {scooter.vin || 'N/A'} &bull; ID: {scooter.scooter_id}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info banner */}
        <div className="flex items-start gap-2 bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
          <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-300">
            Completati fisa de control mecanic. Puteti nota fiecare punct (0-5), selecta actiunea efectuata si adauga fotografii. 
            Apasati <strong>Salveaza</strong> cand ati terminat.
          </p>
        </div>

        {/* Mechanical Checklist Form */}
        <MechanicalChecklistForm
          values={mechanicalCheck}
          onChange={handleCheckChange}
          scooterInfo={scooterInfo}
        />

        {/* Bottom save button */}
        <div className="sticky bottom-4">
          <Button onClick={handleSave} disabled={saving || !hasChanges} className={`w-full h-12 text-base ${hasChanges ? "bg-green-600 hover:bg-green-700 shadow-lg shadow-green-500/20" : ""}`}
            data-testid="save-fiche-bottom">
            {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : hasChanges ? <Save className="w-5 h-5 mr-2" /> : <CheckCircle className="w-5 h-5 mr-2" />}
            {saving ? "Se salveaza fisa tehnica..." : hasChanges ? "Salveaza Fisa Tehnica" : "Fisa tehnica salvata"}
          </Button>
        </div>
      </div>
    </div>
  );
}
